import { Box, Card, Typography } from "@mui/material";

import {
  BudgetDashboardGroupItem,
  BudgetDashboardSummary,
} from "../../../types/dashboard";
import { formatPercent, formatRate } from "../utils";

type InsightTone = "up" | "down" | "warn" | "neutral";

interface Insight {
  tone: InsightTone;
  text: string;
}

interface InsightsPanelProps {
  summary?: BudgetDashboardSummary;
  cells: BudgetDashboardGroupItem[];
}

const TONE_COLORS: Record<InsightTone, string> = {
  up: "#16A34A",
  down: "#DC2626",
  warn: "#D97706",
  neutral: "#64748B",
};

// Mesmas regras do protótipo: variação do período, projetos parados,
// melhor e pior célula, e recomendação para as células em vermelho.
export const buildInsights = (
  summary: BudgetDashboardSummary,
  cells: BudgetDashboardGroupItem[]
): Insight[] => {
  const insights: Insight[] = [];
  const rate = formatPercent(summary.adoptionRate);
  // Sempre com uma casa decimal, como no protótipo: "75,0 p.p.".
  const absDelta = Math.abs(summary.adoptionDelta ?? 0)
    .toFixed(1)
    .replace(".", ",");

  if (summary.adoptionDelta === null) {
    insights.push({
      tone: "neutral",
      text: `A adoção do período é de ${rate} (sem período anterior comparável).`,
    });
  } else if (summary.adoptionDelta > 0) {
    insights.push({
      tone: "up",
      text: `A adoção subiu ${absDelta} p.p. em relação ao período anterior (${rate} no período atual).`,
    });
  } else if (summary.adoptionDelta < 0) {
    insights.push({
      tone: "down",
      text: `A adoção caiu ${absDelta} p.p. em relação ao período anterior (${rate} no período atual).`,
    });
  } else {
    insights.push({
      tone: "neutral",
      text: `A adoção manteve-se estável em relação ao período anterior (${rate}).`,
    });
  }

  if (summary.withoutBudget > 0 && summary.staleCount > 0) {
    const share = Math.round(
      (summary.staleCount / summary.withoutBudget) * 100
    );
    insights.push({
      tone: "warn",
      text: `${summary.staleCount} projeto(s) estão há mais de ${summary.staleDays} dias sem orçamento (${share}% dos projetos sem orçamento).`,
    });
  }

  if (cells.length) {
    const byBest = [...cells].sort(
      (a, b) =>
        b.adoptionRate - a.adoptionRate || b.totalProjects - a.totalProjects
    );
    const byWorst = [...cells].sort(
      (a, b) =>
        a.adoptionRate - b.adoptionRate || b.totalProjects - a.totalProjects
    );

    const best = byBest[0];
    const worst = byWorst[0];

    insights.push({
      tone: "up",
      text: `${best.name} apresenta a maior taxa de adoção (${formatPercent(
        best.adoptionRate
      )}, ${best.totalProjects} projeto(s)).`,
    });

    if (worst.name !== best.name) {
      if (worst.adoptionRate <= 70) {
        insights.push({
          tone: worst.adoptionRate < 40 ? "down" : "warn",
          text: `${worst.name} possui a menor taxa de adoção (${formatPercent(
            worst.adoptionRate
          )}, ${worst.totalProjects} projeto(s)) e merece acompanhamento.`,
        });
      } else {
        insights.push({
          tone: "up",
          text: `Todas as células estão com adoção saudável — a menor é ${
            worst.name
          } (${formatPercent(worst.adoptionRate)}).`,
        });
      }
    }

    const critical = cells
      .filter((cell) => cell.adoptionRate < 40)
      .sort((a, b) => a.adoptionRate - b.adoptionRate);

    if (critical.length) {
      const names = critical.map((cell) => cell.name);
      const list =
        names.length <= 3
          ? names.join(", ")
          : `${names.slice(0, 3).join(", ")} e mais ${names.length - 3}`;

      insights.push({
        tone: "warn",
        text: `Recomenda-se priorizar as ${critical.length} célula(s) em vermelho (${list}) para aumentar a adoção do módulo.`,
      });
    }
  }

  return insights;
};

export const InsightsPanel = ({ summary, cells }: InsightsPanelProps) => {
  if (!summary || summary.totalProjects === 0) {
    return null;
  }

  const insights = buildInsights(summary, cells);

  return (
    <Card
      elevation={0}
      sx={{
        p: "16px 20px",
        mb: "20px",
        borderRadius: "12px",
        background: "linear-gradient(135deg,#F5F2FF,#EEF4FC)",
        border: "1px solid #DDD6F3",
      }}
    >
      <Typography fontSize={14} fontWeight={700} color="#4A2F8F" mb="10px">
        📊 Insights do período
      </Typography>

      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 0,
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {insights.map((insight) => (
          <Box
            component="li"
            key={insight.text}
            sx={{ display: "flex", gap: "11px", alignItems: "flex-start" }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: TONE_COLORS[insight.tone],
                mt: "6px",
                flexShrink: 0,
              }}
            />
            <Typography fontSize={13.5} lineHeight={1.45} color="#1F2733">
              {insight.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};
